import { useCallback, useRef, useState } from 'react'
import { toReturnShipper } from '@/pages/return/coMingle'
import { flushSync } from 'react-dom'
import { scanNotice } from '@/components/pbo/notice'
import { useGetWorkInvoices, useGetWorkItems } from '@/pages/return/rgr11V2/queries/useReturnWorkQueries'
import { WorkInvoiceQuery, WorkItemQuery, WorkTableRow } from '@/pages/return/rgr11V2/queries/types'
import { MESSAGES, SAVE_BARCODE_CHEAT, formatSearchScope, getActDateRange, isIntegrationReturnShipper } from '@/pages/return/rgr11V2/constants'

export interface UseRgr11WorkflowParams {
  /** 로그인 사용자 화주. */
  shipperCode: string
  coMingleEnabled?: boolean
  /** 로그인 사용자 email — HEAD 조회 `userId`(없으면 `ordNoMax` 가 null 로 온다, 3.1). */
  userId: string
  masterFlag: 'N' | 'Y'
  /** 통합반품 화주일 때만 조회 조건에 실린다. */
  centerNm: string
  /** 상품바코드에 `SAVE` 를 스캔했을 때(1.0 치트, 8.1-17) — 화면에 표시하지 않는다. */
  onSaveRequested: () => void
}

/**
 * 반송장·원송장·상품바코드 스캔 → 검수표 적재 상태기계(6절 4단계).
 *
 * 1.0 `RGR11.js` 의 가드·채번을 그대로 옮긴다:
 * - 반송장 Enter → HEAD 조회. 원송장 Enter → 반송장이 있을 때만 HEAD 조회.
 * - 상품바코드 Enter → 반품번호(다음 채번값)가 없으면 차단, 마스터 조회여부 N 이면
 *   반송장·원송장 둘 다 없을 때 차단.
 * - 반품번호 채번은 직전 행 +1, 행이 없으면 HEAD 응답 `ordNoMax`(또는 사용자가 직접 입력한 값).
 * - 합포 불러오기 → 직전 조건으로 HEAD 재조회, 바코드 비움, 서버가 준 `ordNoMax` 로 갱신.
 */
export const useRgr11Workflow = (params: UseRgr11WorkflowParams) => {
  const { shipperCode, userId, masterFlag, centerNm, onSaveRequested, coMingleEnabled = false } = params

  const [rtnInvoiceNo, setRtnInvoiceNo] = useState('')
  const [orgInvoiceNo, setOrgInvoiceNo] = useState('')
  const [barcodeInput, setBarcodeInput] = useState('')
  const [ordNoInput, setOrdNoInput] = useState('')
  const [rows, setRows] = useState<WorkTableRow[]>([])
  const [invoiceQuery, setInvoiceQuery] = useState<WorkInvoiceQuery | null>(null)
  const [itemQuery, setItemQuery] = useState<WorkItemQuery | null>(null)

  const { head, meta, refetch: refetchInvoices, isFetching: isSearchingInvoice } = useGetWorkInvoices(invoiceQuery)
  const { refetch: refetchItems, isFetching: isSearchingItem } = useGetWorkItems(itemQuery)

  /** 직전에 실제로 조회한 조건 — Enter 뒤 그 값 그대로 blur 가 나가는 통상 동선에서 같은 조회를 두 번 태우지 않는다. */
  const lastSearchedRef = useRef<{ rtnInvoiceNo: string; orgInvoiceNo: string } | null>(null)

  /**
   * 지금 작업 중인 배치의 화주. HEAD 를 받기 전에는 로그인 화주고, 받은 뒤에는 스캔한 반송장의
   * 화주다. 통합반품(`INTERGRATION_RETURN`)은 서버가 화주 무필터로 HEAD 를 주기 때문에 이 둘이
   * 실제로 갈린다(계획서 3.1 "화주 범위 — 통합반품이면 무필터", 5절·부록 "통합반품처럼 HEAD 응답의
   * `sprNm` 이 로그인 화주와 다를 수 있으므로").
   *
   * 상품바코드 조회의 `shipper` 는 계약상 HEAD 응답 `sprNm` 이다(3.2) — 이걸 로그인 화주로 바꾸면
   * 통합반품에서 조회가 어긋난다. 그래서 요청을 맞추는 대신 표시를 여기에 맞춘다. 카드 안내와
   * 무결과 안내가 같은 값을 읽으므로 한 시점에 두 화주가 보이는 일이 없다(PR #718 리뷰).
   */
  const effectiveShipper = head?.sprNm || shipperCode

  const buildInvoiceQuery = useCallback((): WorkInvoiceQuery => {
    const { actDateFrom, actDateTo } = getActDateRange()
    return {
      shipper: toReturnShipper(shipperCode, coMingleEnabled),
      rtnInvoiceNo: rtnInvoiceNo || undefined,
      orgInvoiceNo: orgInvoiceNo || undefined,
      actDateFrom,
      actDateTo,
      centerNm: isIntegrationReturnShipper(shipperCode) ? centerNm || undefined : undefined,
      userId,
    }
  }, [shipperCode, rtnInvoiceNo, orgInvoiceNo, centerNm, userId, coMingleEnabled])

  /** HEAD 조회 실행. 무결과(200+빈 배열)는 화면이 직접 안내한다. */
  const runInvoiceSearch = useCallback(async () => {
    // 통합반품 화주가 원송장 분기(orgInvoiceNo 존재)를 태우는데 등록센터가 없으면 400 이다(3.1) —
    // 헛호출을 없애려 요청 조립 전에 막는다(PR #691 리뷰 코멘트).
    if (isIntegrationReturnShipper(shipperCode) && orgInvoiceNo && !centerNm) {
      scanNotice.invalid(MESSAGES.centerNmRequired)
      return
    }
    lastSearchedRef.current = { rtnInvoiceNo, orgInvoiceNo }
    const query = buildInvoiceQuery()
    // queryFilter 커밋 전에 refetch 하면 이전 조건으로 조회된다(returnReceiptInquiry 선례와 같은 이유).
    flushSync(() => setInvoiceQuery(query))
    const result = await refetchInvoices()
    if (result.error) {
      scanNotice.failure(MESSAGES.searchFailed) // 서버 오류 — 무결과 안내와 구분(전역 알림을 끈 쿼리라 여기서 한 번)
      setOrdNoInput('')
      return
    }
    const found = result.data
    if (!found?.head) {
      // 무엇으로 걸렀는지 담는다 — 화주·출고일자는 사용자가 고를 수 없어 그냥 무결과만 띄우면
      // 다른 화주 반송장인지 30일 밖 건인지 구분이 안 된다(LOGISPRD-12613). 실제로 보낸 query 를
      // 그대로 읽어 안내와 요청이 어긋나지 않게 한다.
      scanNotice.failure(MESSAGES.notFoundInScope(formatSearchScope(query)))
      // 이전 반송장의 ordNoMax 가 남아 다음 스캔의 채번을 오염시키지 않게 비운다(PR #691 리뷰 코멘트).
      setOrdNoInput('')
      return
    }
    // 그날 첫 작업이면 서버가 ordNoMax 를 null 로 준다 — 1.0(`RGR11.js` `?? 1`)·RGR17(`useReturnWorkScan`)처럼 1번부터 매긴다(LOGISPRD-14551).
    setOrdNoInput(String(found.meta.ordNoMax ?? 1))
  }, [shipperCode, orgInvoiceNo, centerNm, rtnInvoiceNo, buildInvoiceQuery, refetchInvoices])

  /**
   * 같은 입력칸에 Enter 와 blur 가 모두 걸려 있어(`InvoiceCard` 반송장 입력) 통상 동선(Enter →
   * 다른 칸으로 이동)에서 같은 조건으로 두 번 조회하고, 그 사이 손으로 고친 반품번호가 서버
   * `ordNoMax` 로 되돌아간다(PR #691 리뷰 코멘트). 직전 조회와 조건이 같으면 건너뛴다 — 합포
   * 불러오기(`handlePartialCall`)는 이 가드를 타지 않고 항상 재조회한다(그게 목적이다).
   */
  const isSameAsLastSearch = useCallback(
    () => lastSearchedRef.current?.rtnInvoiceNo === rtnInvoiceNo && lastSearchedRef.current?.orgInvoiceNo === orgInvoiceNo,
    [rtnInvoiceNo, orgInvoiceNo],
  )

  const handleRtnInvoiceEnter = useCallback(async () => {
    if (!rtnInvoiceNo || isSameAsLastSearch()) return
    await runInvoiceSearch()
  }, [rtnInvoiceNo, isSameAsLastSearch, runInvoiceSearch])

  /** 원송장은 반송장이 있을 때만 조회한다(1.0 `onSearchInvoice`). */
  const handleOrgInvoiceEnter = useCallback(async () => {
    if (!rtnInvoiceNo || !orgInvoiceNo || isSameAsLastSearch()) return
    await runInvoiceSearch()
  }, [rtnInvoiceNo, orgInvoiceNo, isSameAsLastSearch, runInvoiceSearch])

  /** 합포 불러오기 진행 중 잠금 — 연타해도 조회는 한 번(LOGISPRD-14894). 렌더와 무관한 ref 라 같은 틱 클릭도 막는다. */
  const partialCallingRef = useRef(false)

  /** 합포 불러오기 — 직전 조건(반송장·원송장)이 있어야 한다(1.0 `originSearchData` 가드). */
  const handlePartialCall = useCallback(async () => {
    if (partialCallingRef.current) return
    if (!rtnInvoiceNo && !orgInvoiceNo) {
      scanNotice.invalid(MESSAGES.partialCallNoCondition)
      return
    }
    setBarcodeInput('')
    partialCallingRef.current = true
    try {
      await runInvoiceSearch()
    } finally {
      partialCallingRef.current = false
    }
  }, [rtnInvoiceNo, orgInvoiceNo, runInvoiceSearch])

  /** 다음에 매길 반품번호 — 표에 행이 있으면 마지막 행 +1, 없으면 반품번호 입력값(=ordNoMax 초기값). */
  const nextOrdNo = useCallback((): string => {
    const lastRow = rows.at(-1)
    if (!lastRow) return ordNoInput
    const last = Number(lastRow.ORD_NO)
    return Number.isNaN(last) ? lastRow.ORD_NO : String(last + 1)
  }, [rows, ordNoInput])

  const handleBarcodeEnter = useCallback(async () => {
    const raw = barcodeInput.trim()
    if (!raw) return

    if (raw.toUpperCase() === SAVE_BARCODE_CHEAT) {
      setBarcodeInput('')
      onSaveRequested()
      return
    }

    const ordNo = nextOrdNo()
    if (!ordNo) {
      scanNotice.invalid(MESSAGES.ordNoRequired)
      return
    }
    if (masterFlag === 'N' && !rtnInvoiceNo && !orgInvoiceNo) {
      scanNotice.invalid(MESSAGES.invoiceOrOrgInvoiceRequired)
      return
    }

    const { actDateFrom, actDateTo } = getActDateRange()
    const query: WorkItemQuery = {
      shipper: toReturnShipper(effectiveShipper, coMingleEnabled),
      flag: masterFlag === 'Y',
      barcode: raw,
      rtnInvoiceNo: rtnInvoiceNo || undefined,
      orgInvoiceNo: orgInvoiceNo || undefined,
      actDateFrom,
      actDateTo,
      giId: meta.giIds,
    }
    flushSync(() => setItemQuery(query))
    const result = await refetchItems()
    if (result.error) {
      scanNotice.failure(MESSAGES.searchFailed) // 서버 오류 — 무결과 안내와 구분
      return
    }
    const found = result.data ?? []
    if (found.length === 0) {
      scanNotice.failure(MESSAGES.notFoundInScope(formatSearchScope({ shipper: effectiveShipper, actDateFrom, actDateTo })))
      return
    }

    const cursor = Number(ordNo)
    const useSequential = !Number.isNaN(cursor)
    const timestamp = Date.now()
    const computeOrdNo = (index: number): string => {
      if (index === 0) return ordNo
      return useSequential ? String(cursor + index) : ordNo
    }
    // 새 행 추가 시 직전 행의 반품처를 이어받는다 — 1.0 onRowAddOption=['PLN_DATE','ORD_OPT_NO',
    // 'RL_CHANNEL','ORD_NO'](RGR11.js:1082) 재현. PLN_DATE·ORD_OPT_NO 는 이 화면에 대응 컬럼이
    // 없고 ORD_NO 는 이미 별도로 채번하므로, 실제로 옮길 값은 반품처뿐이다.
    const prevRlChannel = rows.at(-1)?.RL_CHANNEL || ''
    const appended = found.map((row, i) => ({
      ...row,
      ORD_NO: computeOrdNo(i),
      key: `row-${timestamp}-${i}`,
      RL_CHANNEL: prevRlChannel,
    }))
    setRows((prev) => [...prev, ...appended])
    setBarcodeInput('')
  }, [barcodeInput, nextOrdNo, masterFlag, rtnInvoiceNo, orgInvoiceNo, effectiveShipper, meta, refetchItems, onSaveRequested, rows, coMingleEnabled])

  /**
   * 저장 성공 후 입력 초기화(6절 5단계 — 반송장 포커스는 호출부 책임). `keepRows` 는 저장에서
   * 제외된 중복 반품번호 행 — 표를 통째로 비우지 않고 남겨 사용자가 알아볼 수 있게 한다(PR #691
   * 리뷰 코멘트).
   */
  const resetAfterSave = useCallback((keepRows: WorkTableRow[] = []) => {
    setRtnInvoiceNo('')
    setOrgInvoiceNo('')
    setBarcodeInput('')
    setOrdNoInput('')
    setRows(keepRows)
    setInvoiceQuery(null)
    setItemQuery(null)
    lastSearchedRef.current = null
  }, [])

  /**
   * 화면에 띄우는 조회 범위(LOGISPRD-12613) — 무결과 안내와 같은 문구를 쓴다. `buildInvoiceQuery`
   * 를 그대로 통과시켜 표시와 실제 요청이 갈라지지 않게 하고, memo 로 굳히지 않는다. 굳히면
   * 자정을 넘긴 세션에서 `getActDateRange` 만 움직이고 표시가 어제 범위에 남는다.
   *
   * 화주만 `effectiveShipper` 로 덮는다. HEAD 가 없을 때는 로그인 화주라 반송장 조회 요청과 같고,
   * HEAD 를 받은 뒤에는 상품바코드 조회 요청과 같다. 반송장 무결과는 HEAD 가 null 인 시점에만
   * 뜨므로 세 자리(카드·반송장 안내·바코드 안내)가 서로 다른 화주를 말하지 않는다.
   */
  const searchScope = formatSearchScope({ ...buildInvoiceQuery(), shipper: toReturnShipper(effectiveShipper, coMingleEnabled) })

  return {
    searchScope,
    rtnInvoiceNo,
    setRtnInvoiceNo,
    orgInvoiceNo,
    setOrgInvoiceNo,
    barcodeInput,
    setBarcodeInput,
    ordNoInput,
    setOrdNoInput,
    head,
    meta,
    rows,
    setRows,
    isSearching: isSearchingInvoice || isSearchingItem,
    handleRtnInvoiceEnter,
    handleOrgInvoiceEnter,
    handleBarcodeEnter,
    handlePartialCall,
    resetAfterSave,
  }
}
